<template>
  <div class="bim-page">
    <!-- Selector de fuente: IFC local (Xeokit) o Autodesk ACC -->
    <div class="bim-source-tabs">
      <button
        type="button"
        class="bim-source-tab"
        :class="{ active: activeSource === 'ifc' }"
        @click="activeSource = 'ifc'"
      >
        <i class="fa fa-cubes"></i>
        <span>{{ $t('bim_source_ifc') }}</span>
      </button>
      <button
        type="button"
        class="bim-source-tab"
        :class="{ active: activeSource === 'acc' }"
        @click="activeSource = 'acc'"
      >
        <i class="fa fa-cloud"></i>
        <span>{{ $t('bim_source_acc') }}</span>
      </button>
    </div>

    <!-- Flujo IFC (Xeokit) — existente, sin cambios -->
    <template v-if="activeSource === 'ifc'">
      <div class="bim-topbar">
        <button class="button is-primary is-small" @click="$refs.ifcModal.show()">
          {{ $t('select_ifc') }}
        </button>

        <div class="bim-topbar-filters" v-if="hasModelLoaded">
          <span class="bim-topbar-filters-label">{{ $t('bim_progress_filter') }}:</span>
          <button
            v-for="c in progressColorPresets"
            :key="c.value"
            type="button"
            class="bim-topbar-filter"
            :class="{ active: progressFilter === c.value }"
            @click="toggleFilter(c.value)"
          >
            <span class="bim-topbar-filter-dot" :style="{ background: c.value }"></span>
            <span class="bim-topbar-filter-lbl">{{ c.label }}</span>
            <span class="bim-topbar-filter-count">{{ progressCounts[c.value] || 0 }}</span>
          </button>
          <button
            v-if="progressFilter"
            type="button"
            class="bim-topbar-filter-clear"
            @click="clearFilter"
            :title="$t('bim_progress_filter_clear')"
          >
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>

      <ifc-document-selector-modal
        ref="ifcModal"
        :projectId="project.id"
        @document-selected="handleDocumentSelected"
      />

      <div class="bim-viewer-wrap">
        <ifc-xkt-viewer
          ref="viewer"
          :url="ifcUrl"
          :urls="ifcUrls"
          :federated="ifcFederated"
          :documentId="ifcDocumentId"
          :documentIds="ifcDocumentIds"
          @progress-counts="handleProgressCounts"
          @progress-filter="handleProgressFilter"
        />
      </div>
    </template>

    <!-- Flujo ACC (Autodesk Platform Services) — nuevo -->
    <template v-else-if="activeSource === 'acc'">
      <div class="bim-acc-layout" :class="{ 'browser-collapsed': accBrowserCollapsed }">
        <div class="bim-acc-browser">
          <div class="bim-acc-browser-header">
            <button
              type="button"
              class="button is-small is-light"
              :title="$t('autodesk.resources.manage')"
              @click="showResourcesConfig = true"
            >
              <b-icon pack="fas" icon="cog" size="is-small" />
              <span>{{ $t('autodesk.resources.manage') }}</span>
            </button>
          </div>
          <acc-project-browser
            :project-id="project.id"
            @item-selected="handleAccItemSelected"
          />
        </div>
        <div class="bim-acc-viewer">
          <button
            type="button"
            class="bim-acc-toggle-browser-btn"
            :title="$t(accBrowserCollapsed ? 'bim_acc_show_browser' : 'bim_acc_hide_browser')"
            @click="accBrowserCollapsed = !accBrowserCollapsed"
          >
            <b-icon pack="fas" :icon="accBrowserCollapsed ? 'chevron-right' : 'chevron-left'" size="is-small" />
          </button>
          <aps-viewer
            v-if="selectedAccUrn"
            :key="selectedAccUrn"
            :urn="selectedAccUrn"
            :project-id="project.id"
            :region="selectedAccRegion"
          />
          <div v-else class="bim-acc-viewer-empty">
            <i class="fa fa-eye"></i>
            <p>{{ $t('bim_acc_select_file_hint') }}</p>
          </div>
        </div>
      </div>

      <!-- Drawer admin: gestionar recursos ACC del proyecto -->
      <autodesk-project-resources
        :visible="showResourcesConfig"
        :project-id="project.id"
        :project-name="project.name"
        @close="showResourcesConfig = false"
      />
    </template>

  </div>
</template>

<script type="module">

// import myAwesomeExtension from '../../components/autodesk/viewer/extensions/myAwesomeExtension.js';
// import myCustomToolbar from '../../components/autodesk/viewer/extensions/myCustomToolbar.js';
// import ViewerForge from '../../components/autodesk/viewer/viewer-forge.vue'
// import ModelPreviewList from '../../components/autodesk/model-preview-list.vue'

import {mapGetters} from 'vuex'
import axios from 'axios';
import ifcDocumentSelectorModal from '../../components/xeokit/ifc-document-selector-modal.vue';
import ifcXktViewer from '../../components/xeokit/ifc-xkt-viewer.vue';
import apsViewer from '../../components/autodesk/aps-viewer.vue';
import accProjectBrowser from '../../components/autodesk/acc-project-browser.vue';
import autodeskProjectResources from '../../components/autodesk/autodesk-project-resources.vue';


export default {
  middleware: 'auth',
  title: 'project_design_viewer_page_title',
  subtitle: 'project_design_viewer_page_subtitle',
  menuOption: '2-10',

  components: {
    ifcDocumentSelectorModal,
    ifcXktViewer,
    apsViewer,
    accProjectBrowser,
    autodeskProjectResources,
  },

  data() {
    return {
      // urn:'dXJuOmFkc2sub2JqZWN0czpvcy5vYmplY3Q6cHJ1ZWJhX215X2ZpcnN0X2J1Y2tldC9TdXNwZW5zaW9uLnppcA==',
      // urn:'dXJuOmFkc2sub2JqZWN0czpvcy5vYmplY3Q6YnVja2V0X2VwY19wcm9qZWN0XzIyMy9UZWNobmljYWxfc2Nob29sLWN1cnJlbnRfbS5ydnQ',
      urn: null,

      activeSource: 'ifc',

      options: {
        // env: 'AutodeskProduction2',
        // env: 'AutodeskProduction2',
        // api: 'streamingV2',  // for models uploaded to EMEA change this option to 'streamingV2_EU'
      },
      // ifcUrl:  URL de un IFC individual (string). Se usa cuando se selecciona un IFC individual.
      ifcUrl: null,
      // ifcUrls: Array de URLs para un modelo IFC federado. Solo uno de los dos es no-null a la vez.
      ifcUrls: null,
      // ifcFederated: true = mismo edificio (superponer), false = edificios distintos (lado a lado)
      ifcFederated: false,
      // ifcDocumentId: ID del IfcDocument individual seleccionado (null para federados)
      ifcDocumentId: null,
      // ifcDocumentIds: IDs de los IfcDocuments cuando se carga un federado.
      ifcDocumentIds: null,
      // Espejo del estado del filtro de avance expuesto por el viewer.
      progressCounts: {},
      progressFilter: null,

      // Selección actual desde el navegador ACC.
      selectedAccUrn: null,
      selectedAccRegion: 'EMEA',
      accBrowserCollapsed: false,
      // Modal admin de configuración de recursos ACC del proyecto.
      showResourcesConfig: false,
    }
  },

  mounted() {
    this.getProjectModels()
    // this.$refs.viewer.addExtension(myAwesomeExtension);
    // this.$refs.viewer.addExtension(myCustomToolbar);
  },

  computed: {
    ...mapGetters({
      user: "auth/user",
      project: 'app/project',
      company: "app/company",
    }),

    progressColorPresets() {
      return [
        { value: '#9e9e9e', label: this.$t('bim_progress_status_not_started') },
        { value: '#f9c72c', label: this.$t('bim_progress_status_in_progress') },
        { value: '#4caf50', label: this.$t('bim_progress_status_completed') },
        { value: '#f44336', label: this.$t('bim_progress_status_issue') },
      ];
    },

    // Mostramos los filtros en cuanto haya un modelo cargado, aunque los
    // contadores estén a 0 — así el usuario ve los chips y sabe que existen.
    hasModelLoaded() {
      return !!(this.ifcUrl || (this.ifcUrls && this.ifcUrls.length > 0));
    },
  },

  beforeCreate() {
    // const script = document.createElement('script');
    // script.src = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/viewer3D.min.js';
    // script.async = true;
    // script.onload = this.getLoad;

    // document.head.appendChild(script);
  },

  created() {

  },

  mounted() {
    const q = this.$route.query.source;
    if (q === 'ifc' || q === 'acc') {
      this.activeSource = q;
    }
  },


  methods: {

    handleAccessToken: async function (onSuccess) {
      await axios.get('/api/v2/autodesk/forge/getToken')
        .then((response) => {
          // console.log('res: ', await data.json())
          let data = response.data
          if (! data.success) {
              throw new Error('Error al obtener token.');
          }
          onSuccess(data.data.accessToken, data.data.expiry);
        })
        .catch ((err) => {
          alert('Could not obtain access token. See the console for more details.');
          console.error(err);
        })
    },

    handleDocumentSelected(val) {
      if (val && typeof val === 'object' && val.urls) {
        // Modelo IFC federado: { urls, federated, documentIds }
        this.ifcUrls       = val.urls
        this.ifcFederated  = val.federated
        this.ifcUrl        = null
        this.ifcDocumentId = null
        this.ifcDocumentIds = val.documentIds || null
      } else if (val && typeof val === 'object' && val.url) {
        // IFC individual nuevo formato: { url, documentId }
        this.ifcUrl        = val.url
        this.ifcDocumentId = val.documentId || null
        this.ifcUrls       = null
        this.ifcFederated  = false
        this.ifcDocumentIds = null
      } else if (Array.isArray(val)) {
        // Retrocompatibilidad: array sin flag
        this.ifcUrls       = val
        this.ifcFederated  = true
        this.ifcUrl        = null
        this.ifcDocumentId = null
        this.ifcDocumentIds = null
      } else {
        // Retrocompatibilidad: IFC individual como string
        this.ifcUrl        = val
        this.ifcUrls       = null
        this.ifcFederated  = false
        this.ifcDocumentId = null
        this.ifcDocumentIds = null
      }
    },

    handleChangeModel(urn) {
      this.urn = urn
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    handleProgressCounts(counts) {
      this.progressCounts = counts || {};
    },

    handleProgressFilter(color) {
      this.progressFilter = color || null;
    },

    toggleFilter(color) {
      this.$refs.viewer?.toggleColorFilter?.(color);
    },

    clearFilter() {
      this.$refs.viewer?.clearColorFilter?.();
    },

    // El navegador ACC emite este evento cuando el usuario selecciona un item + versión.
    // payload: { item, version, urn, region }
    // La región (US/EMEA/AUS) viene del hub padre y debe llegar al viewer para que
    // ataque el CDN correcto de Model Derivative — si no, 401 en el manifest.
    handleAccItemSelected(payload) {
      if (payload && payload.urn) {
        this.selectedAccUrn = payload.urn;
        if (payload.region) {
          this.selectedAccRegion = payload.region;
        }
      }
    },

  },
};
</script>

<style scoped>
.bim-page {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 160px);
  margin: -16px;
}

.bim-viewer-wrap {
  flex: 1 1 auto;
  min-height: 0;
}

/* Topbar muy compacta: pegada al título, sin padding vertical extra. */
.bim-topbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 10px;
  flex-wrap: wrap;
  background: #fff;
  border-bottom: 1px solid #e4e8ef;
}

.bim-topbar-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
}

.bim-topbar-filters-label {
  font-size: 11px;
  font-weight: 600;
  color: #5d6b82;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

@media (max-width: 1024px) {
  .bim-topbar-filter {
    padding: 4px 8px;
    font-size: 11px;
  }
}

@media (max-width: 640px) {
  .bim-topbar {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .bim-topbar-filters {
    margin-left: 0;
    width: 100%;
  }
  .bim-topbar-filters-label {
    width: 100%;
    margin-bottom: 2px;
  }
}

.bim-topbar-filter {
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border: 1.5px solid #dde2ea;
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
  font-size: 11px;
  color: #2f374a;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
}

.bim-topbar-filter:hover {
  background: #f5f7fb;
  border-color: #c5ccd8;
}

.bim-topbar-filter.active {
  background: #eef1f6;
  border-color: #2f374a;
  font-weight: 600;
  box-shadow: 0 0 0 2px rgba(47, 55, 74, 0.08);
}

.bim-topbar-filter-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
}

.bim-topbar-filter-count {
  background: #eaeef3;
  color: #5d6b82;
  font-weight: 700;
  font-size: 10px;
  padding: 1px 7px;
  border-radius: 10px;
  min-width: 22px;
  text-align: center;
}

.bim-topbar-filter.active .bim-topbar-filter-count {
  background: #2f374a;
  color: #fff;
}

.bim-topbar-filter-clear {
  all: unset;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f44336;
  color: #fff;
  border-radius: 50%;
  cursor: pointer;
  font-size: 11px;
  transition: background 0.15s;
}

.bim-topbar-filter-clear:hover {
  background: #d32f2f;
}

/* Tabs de fuente (IFC / ACC) — compactos, alineados con la barra existente. */
.bim-source-tabs {
  display: flex;
  gap: 4px;
  padding: 6px 10px 0;
  background: #fff;
  border-bottom: 1px solid #e4e8ef;
}

.bim-source-tab {
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px 8px;
  border: 1.5px solid transparent;
  border-bottom: none;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: #5d6b82;
  background: #f5f7fb;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.bim-source-tab:hover:not(.active) {
  background: #eef1f6;
  color: #2f374a;
}

.bim-source-tab.active {
  background: #fff;
  color: #2f374a;
  border-color: #e4e8ef;
  position: relative;
  z-index: 1;
  margin-bottom: -1px;
}

.bim-acc-layout {
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 10px;
  min-height: 0;
  padding: 10px;
  background: #f5f7fb;
  transition: grid-template-columns 0.25s ease, gap 0.25s ease;
}

.bim-acc-layout.browser-collapsed {
  grid-template-columns: 0 1fr;
  gap: 0;
}

.bim-acc-layout.browser-collapsed .bim-acc-browser {
  visibility: hidden;
  overflow: hidden;
}

/* Botón flotante para colapsar/expandir el panel de archivos ACC. */
.bim-acc-toggle-browser-btn {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 30;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: none;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.bim-acc-toggle-browser-btn:hover {
  background: rgba(0, 0, 0, 0.8);
}

.bim-acc-browser {
  overflow: auto;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.bim-acc-viewer {
  position: relative;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.bim-acc-viewer-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 400px;
  color: #999;
  font-size: 14px;
  gap: 12px;
}

.bim-acc-viewer-empty i {
  font-size: 3rem;
  color: #ccc;
}

@media (max-width: 960px) {
  .bim-acc-layout {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }
}
</style>
